import { useCallback } from 'react';
import { getMessageFromActions } from 'js/hooks/server';
import { useSmartClosePopups } from 'js/components/common/PopupManager';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { useTwoFactorAuthenticationPopup } from 'js/components/popups/TwoFactorAuthenticationPopup';

import { useReLogin } from './useReLogin';
import { useLogout } from '../../useLogout';
import { VALIDATION_TYPE } from '../constants';
import { useContinueLogin } from './useContinueLogin';
import { useValidationManager } from './useValidationManager';

export const useValidate2FA = (validationsRef) => {
    const logout = useLogout();
    const continueLogin = useContinueLogin();
    const reLogin = useReLogin(validationsRef);
    const closePopups = useSmartClosePopups();
    const showTwoFactorAuthenticationPopup = useTwoFactorAuthenticationPopup();
    const {
        validationStart, validationDone, isValidationInProcess, areAllValidationsDone
    } = useValidationManager(VALIDATION_TYPE.TWO_FACTOR_AUTHENTICATION, validationsRef);

    return useCallback(function validate2FA(pasLoginResponse, credentials, options) {
        const { sessionValidationData = {} } = pasLoginResponse?.data || {};

        if (!sessionValidationData.SessionValidationBy2FAData) {
            validationDone();

            return;
        }

        if (isValidationInProcess()) {
            return;
        }

        let ensureLogout = true;
        const { afterRegisterLogin, isAfterLoginWithCredentials } = options || {};

        validationStart();

        showTwoFactorAuthenticationPopup({
            id: VALIDATION_TYPE.TWO_FACTOR_AUTHENTICATION,
            title: getMessageFromActions(pasLoginResponse),
            onSuccess: async () => {
                validationDone();

                if (areAllValidationsDone()) {
                    await continueLogin({ credentials, afterRegisterLogin, isAfterLoginWithCredentials });
                }

                ensureLogout = false;
            },
            onClose: () => {
                if (ensureLogout) {
                    logout(true);
                    closePopups();
                }
            },
            onCancel: () => {
                publicEventsAPIService.trigger('2fa-popup-closed', 'login');
            },
            resendHandler: () => reLogin({ credentials, options, validate2FA })
        });
    }, [
        logout, continueLogin, reLogin, showTwoFactorAuthenticationPopup, closePopups,
        validationStart, validationDone, isValidationInProcess, areAllValidationsDone
    ]);
};
